/* Dawn's product-form, plus recovery when /cart/add fails.
 *
 * The cart endpoint sits behind Cloudflare and nginx on this store, and about
 * one add in twelve has come back as a 504 with an HTML body. Dawn parsed that
 * body as JSON, the parse threw, and the only handler was console.error: the
 * button stopped spinning and the customer saw nothing. This version
 *   1. treats a network error, a 5xx, or a non-JSON body as "outcome unknown",
 *   2. reads /cart.js and compares it with a snapshot taken at page load, so an
 *      add that did land is taken to the cart instead of being added twice,
 *   3. otherwise retries the POST once, and
 *   4. if that also fails, shows the form's own error message so the customer
 *      can act, instead of leaving the page silent.
 * Real rejections (a 422 with a JSON description, e.g. sold out) behave as in
 * Dawn. Every recovery, successful retry and final failure is reported to GA4
 * as a `cart_add_issue` event (params: outcome, add_path, detail) so the rate
 * can be watched. Renamed from product-form.js because the edge cache keys
 * assets by path and ignores the ?v= fingerprint.
 */
if (!customElements.get('product-form')) {
  const ADD_FAILED_MESSAGE = 'We could not add this to your cart. Please try again.';
  const trackCartAddIssue = (outcome, detail) => {
    try {
      const params = { outcome, add_path: 'product_form', detail: String(detail || '').slice(0, 80), page_path: location.pathname };
      if (typeof window.gtag === 'function') window.gtag('event', 'cart_add_issue', params);
      else if (window.dataLayer) window.dataLayer.push(Object.assign({ event: 'cart_add_issue' }, params));
    } catch (e) { /* tracking must never break the add */ }
  };

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Quantity per variant at load, so a failed add can be told apart from
        // one that landed without a usable response.
        this.cartSnapshot = null;
        this.snapshotCart();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.addToCart(config, formData)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (response.recovered || !this.cart) {
              // A recovered add has no section HTML to paint, so the cart page
              // is the honest place to show what happened.
              window.location = window.routes.cart_url;
              return;
            }

            this.snapshotCart();
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // Both attempts failed and the line is not in the cart. Say so.
            console.error(e);
            trackCartAddIssue('failed', e && e.message);
            this.handleErrorMessage(ADD_FAILED_MESSAGE, true);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // One POST. On a network error, a 5xx, or a body that is not JSON, look
      // at the cart: if the line landed, report that; if not, try once more.
      addToCart(config, formData, attempt = 0) {
        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            const isJson = (response.headers.get('content-type') || '').includes('json');
            if (response.ok || (isJson && response.status < 500)) return response.json();
            throw new Error(`cart/add returned ${response.status}`);
          })
          .catch((error) => {
            if (attempt > 0) throw error;
            return this.addLanded(formData).then((landed) => {
              if (landed) {
                trackCartAddIssue('recovered', error.message);
                return { recovered: true };
              }
              return new Promise((resolve) => setTimeout(resolve, 1000))
                .then(() => this.addToCart(config, formData, attempt + 1))
                .then((result) => {
                  trackCartAddIssue('retried_ok', error.message);
                  return result;
                });
            });
          });
      }

      snapshotCart() {
        return this.readCartQuantities().then((quantities) => {
          this.cartSnapshot = quantities;
          return quantities;
        });
      }

      readCartQuantities() {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (!cart || !cart.items) return null;
            const quantities = {};
            cart.items.forEach((item) => {
              quantities[item.variant_id] = (quantities[item.variant_id] || 0) + item.quantity;
            });
            return quantities;
          })
          .catch(() => null);
      }

      addLanded(formData) {
        const variantId = formData.get('id');
        const before = this.cartSnapshot;
        return this.readCartQuantities().then((now) => {
          if (!now) return false;
          const quantityNow = now[variantId] || 0;
          if (!before) return quantityNow > 0;
          return quantityNow > (before[variantId] || 0);
        });
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) {
          if (errorMessage && force) window.alert(errorMessage);
          return;
        }
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
